/**
 * High-Volume Sound & Audio Synthesizer for Real-Time Order Notifications
 * Uses Web Audio API with multi-oscillator harmonics, dynamic compression & maximum loudness.
 * Rings repeatedly for 10.5 seconds on incoming orders or test clicks, or until stopped.
 */

class NotificationAudioPlayer {
  private audioCtx: AudioContext | null = null;
  private compressor: DynamicsCompressorNode | null = null;
  private masterGain: GainNode | null = null;
  private isMuted: boolean = false;
  private loopIntervalId: any = null;
  private loopStopTimeoutId: any = null;

  constructor() {
    // Load sound preference from localStorage (default: unmuted)
    const savedMute = localStorage.getItem('order_sound_muted');
    this.isMuted = savedMute === 'true';

    // Unlock audio context on first user interaction anywhere on the document
    if (typeof window !== 'undefined') {
      const unlockAudio = () => {
        this.ensureAudioContext();
        window.removeEventListener('click', unlockAudio);
        window.removeEventListener('keydown', unlockAudio);
        window.removeEventListener('touchstart', unlockAudio);
      };
      window.addEventListener('click', unlockAudio, { once: true });
      window.addEventListener('keydown', unlockAudio, { once: true });
      window.addEventListener('touchstart', unlockAudio, { once: true });
    }
  }

  private async ensureAudioContext(): Promise<AudioContext | null> {
    if (typeof window === 'undefined') return null;

    if (!this.audioCtx) {
      const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioCtxClass) {
        this.audioCtx = new AudioCtxClass();
      }
    }

    if (this.audioCtx) {
      if (this.audioCtx.state === 'suspended') {
        try {
          await this.audioCtx.resume();
        } catch {}
      }

      if (!this.compressor || !this.masterGain) {
        // Dynamic Range Compressor to maximize volume loudness (LUFS) without clipping
        this.compressor = this.audioCtx.createDynamicsCompressor();
        this.compressor.threshold.setValueAtTime(-6, this.audioCtx.currentTime);
        this.compressor.knee.setValueAtTime(6, this.audioCtx.currentTime);
        this.compressor.ratio.setValueAtTime(16, this.audioCtx.currentTime);
        this.compressor.attack.setValueAtTime(0.002, this.audioCtx.currentTime);
        this.compressor.release.setValueAtTime(0.2, this.audioCtx.currentTime);

        // Master output gain (Maximum volume 100%)
        this.masterGain = this.audioCtx.createGain();
        this.masterGain.gain.setValueAtTime(1.0, this.audioCtx.currentTime);

        this.compressor.connect(this.masterGain);
        this.masterGain.connect(this.audioCtx.destination);
      }
    }

    return this.audioCtx;
  }

  public isSoundMuted(): boolean {
    return this.isMuted;
  }

  public setSoundMuted(muted: boolean): void {
    this.isMuted = muted;
    localStorage.setItem('order_sound_muted', String(muted));
    if (muted) {
      this.stopOrderChime();
    }
  }

  public toggleSoundMuted(): boolean {
    const next = !this.isMuted;
    this.setSoundMuted(next);
    if (!next) {
      // Play the full 10-second order alarm so the user can test the exact sound!
      this.playOrderChime(10500);
    } else {
      this.stopOrderChime();
    }
    return next;
  }

  /**
   * Stop any actively playing order alarm
   */
  public stopOrderChime(): void {
    if (this.loopIntervalId) {
      clearInterval(this.loopIntervalId);
      this.loopIntervalId = null;
    }
    if (this.loopStopTimeoutId) {
      clearTimeout(this.loopStopTimeoutId);
      this.loopStopTimeoutId = null;
    }
    if (this.masterGain && this.audioCtx) {
      this.masterGain.gain.setValueAtTime(0, this.audioCtx.currentTime);
      setTimeout(() => {
        if (this.masterGain && this.audioCtx && !this.isMuted) {
          this.masterGain.gain.setValueAtTime(1.0, this.audioCtx.currentTime);
        }
      }, 150);
    }
  }

  /**
   * Play continuous, high-volume order alarm that loops every 1.8s for ~10.5 seconds
   */
  public async playOrderChime(durationMs = 10500): Promise<void> {
    if (this.isMuted) return;

    this.stopOrderChime();

    try {
      const ctx = await this.ensureAudioContext();
      if (!ctx || !this.compressor || !this.masterGain) return;

      this.masterGain.gain.setValueAtTime(1.0, ctx.currentTime);

      // 1. Play first cycle immediately
      this.playChimeBurstNow(ctx);

      // 2. Loop subsequent cycles every 1.8 seconds
      let elapsed = 0;
      this.loopIntervalId = setInterval(() => {
        elapsed += 1800;
        if (elapsed >= durationMs) {
          this.stopOrderChime();
          return;
        }
      }, 1800);

      // 3. Auto-stop safeguard
      this.loopStopTimeoutId = setTimeout(() => {
        this.stopOrderChime();
      }, durationMs + 300);
    } catch (e) {
      console.warn('[AudioPlayer] Could not play chime:', e);
    }
  }

  /**
   * Play a pleasant 3-note ascending chime for new user registration (C5 -> E5 -> G5)
   */
  public async playUserRegistrationChime(): Promise<void> {
    if (this.isMuted) return;

    this.stopOrderChime();

    try {
      const ctx = await this.ensureAudioContext();
      if (!ctx || !this.compressor || !this.masterGain) return;

      this.masterGain.gain.setValueAtTime(1.0, ctx.currentTime);
      const now = ctx.currentTime;

      // Soft, pleasant 3-tone welcome chime
      this.triggerChimeBurst(ctx, now, 523.25, 1046.50, 0.25);
      this.triggerChimeBurst(ctx, now + 0.12, 659.25, 1318.51, 0.25);
      this.triggerChimeBurst(ctx, now + 0.24, 783.99, 1567.98, 0.45);
    } catch (e) {
      console.warn('[AudioPlayer] Could not play user registration chime:', e);
    }
  }

  /**
   * Play a soft single chime for general system notifications
   */
  public async playGeneralNotificationChime(): Promise<void> {
    if (this.isMuted) return;

    this.stopOrderChime();

    try {
      const ctx = await this.ensureAudioContext();
      if (!ctx || !this.compressor || !this.masterGain) return;

      this.masterGain.gain.setValueAtTime(1.0, ctx.currentTime);
      const now = ctx.currentTime;

      this.triggerChimeBurst(ctx, now, 659.25, 1318.51, 0.30);
    } catch (e) {
      console.warn('[AudioPlayer] Could not play general notification chime:', e);
    }
  }

  /**
   * Play a distinct, loud 3-cycle double-ping chime sequence for Live Support chat initiation
   */
  public async playLiveSupportChime(): Promise<void> {
    if (this.isMuted) return;

    this.stopOrderChime();

    try {
      const ctx = await this.ensureAudioContext();
      if (!ctx || !this.compressor || !this.masterGain) return;

      this.masterGain.gain.setValueAtTime(1.0, ctx.currentTime);

      // Play 3 repeated chime cycles over ~4.5 seconds for high audibility
      for (let i = 0; i < 3; i++) {
        const cycleOffset = i * 1.35;
        const now = ctx.currentTime + cycleOffset;
        this.triggerChimeBurst(ctx, now, 880.0, 1760.0, 0.25);
        this.triggerChimeBurst(ctx, now + 0.15, 1046.5, 2093.0, 0.35);
        this.triggerChimeBurst(ctx, now + 0.50, 1318.51, 2637.0, 0.45);
      }
    } catch (e) {
      console.warn('[AudioPlayer] Could not play live support chime:', e);
    }
  }

  /**
   * Play a single acoustic chime burst cycle right now
   */
  private playChimeBurstNow(ctx: AudioContext): void {
    const now = ctx.currentTime;

    // 🛒 FULL HIGH-PENETRATION STORE POS CHIME BURST
    // Ding 1
    this.triggerChimeBurst(ctx, now, 1046.5, 1567.98, 0.35);
    this.triggerChimeBurst(ctx, now + 0.15, 1318.51, 1975.53, 0.45);

    // Ding 2
    this.triggerChimeBurst(ctx, now + 0.60, 1046.5, 1567.98, 0.35);
    this.triggerChimeBurst(ctx, now + 0.75, 1318.51, 1975.53, 0.45);

    // Ding 3 (Resonant finale chime)
    this.triggerChimeBurst(ctx, now + 1.20, 1567.98, 2349.32, 0.55);
    this.triggerChimeBurst(ctx, now + 1.35, 2093.0, 3135.96, 0.75);
  }

  /**
   * Trigger a layered, high-penetration acoustic chime burst
   */
  private triggerChimeBurst(
    ctx: AudioContext,
    startTime: number,
    freqBase: number,
    freqHarmonic: number,
    duration: number
  ) {
    if (!this.compressor) return;

    // 1. Primary Rich Bell Tone (Square-Triangle blend for maximum crisp penetration)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'triangle';
    osc1.frequency.setValueAtTime(freqBase, startTime);

    // 2. High Shimmer Overtone
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(freqHarmonic, startTime);

    // 3. Crisp Attack transient (gives sharp "ding" attack heard across rooms)
    const osc3 = ctx.createOscillator();
    const gain3 = ctx.createGain();
    osc3.type = 'sine';
    osc3.frequency.setValueAtTime(freqBase * 2.5, startTime);

    // Envelopes with high output gain
    gain1.gain.setValueAtTime(0.001, startTime);
    gain1.gain.linearRampToValueAtTime(0.9, startTime + 0.008);
    gain1.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);

    gain2.gain.setValueAtTime(0.001, startTime);
    gain2.gain.linearRampToValueAtTime(0.7, startTime + 0.006);
    gain2.gain.exponentialRampToValueAtTime(0.0001, startTime + duration * 0.9);

    gain3.gain.setValueAtTime(0.001, startTime);
    gain3.gain.linearRampToValueAtTime(0.5, startTime + 0.003);
    gain3.gain.exponentialRampToValueAtTime(0.0001, startTime + 0.06);

    // Connect to dynamic compressor -> master output
    osc1.connect(gain1);
    osc2.connect(gain2);
    osc3.connect(gain3);

    gain1.connect(this.compressor);
    gain2.connect(this.compressor);
    gain3.connect(this.compressor);

    osc1.start(startTime);
    osc1.stop(startTime + duration + 0.05);

    osc2.start(startTime);
    osc2.stop(startTime + duration + 0.05);

    osc3.start(startTime);
    osc3.stop(startTime + 0.1);
  }
}

export const audioPlayer = new NotificationAudioPlayer();
